import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  getInitialSession,
  listenToAuthChanges,
  signInWithEmail,
  signOutCurrentUser,
  signUpWithEmail,
} from "../../services/supabase/authService";
import { isMissingAuthSessionError, MISSING_AUTH_SESSION_MESSAGE } from "../../services/supabase/authErrors";
import {
  createCompanyWithInitialBranch,
  createInitialBranchForCompany,
  getWorkspaceForUser,
} from "../../services/supabase/restaurantsService";
import {
  acceptPendingInvitationsForUser,
  getCurrentUserProfile,
} from "../../services/supabase/companyAdminService";
import {
  developerBypassAuth,
  isDeveloperBypassAllowed,
  isSupabaseConfigured,
  missingSupabaseConfig,
} from "../../services/supabase/config";
import { mapWaiterPermissionsFromBranchPermissions } from "../../services/local/waiterPermissions";
import {
  isMerchantWorkspaceUpdateForUser,
  subscribeToMerchantWorkspaceUpdates,
} from "../../services/local/merchantWorkspaceEvents";
import {
  getRestaurantProfile,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import { isPlatformBackofficeAdminUser } from "../../services/supabase/backofficeService";
import {
  buildMozzoRoleBranchPermissions,
  canAccessMozzoModule,
  getMozzoInternalUserRole,
  resolveMozzoWorkspaceAccessRole,
} from "../../services/local/accessControl";
import {
  clearActiveLocalPersonnelTestProfile,
  isLocalPersonnelTestHarnessEnabled,
  persistActiveLocalPersonnelTestProfile,
  readActiveLocalPersonnelTestProfile,
} from "../../services/local/personnelTestHarness";

const AuthContext = createContext();
const CREATOR_MODE_KEY = "mozzoapp:creator-mode";
const WAITER_DEMO_MODE_KEY = "mozzoapp:waiter-demo-mode";
const WAITER_DEMO_WORKSPACE_KEY = "mozzoapp:waiter-demo-workspace";
const ACTIVE_BRANCH_KEY_PREFIX = "mozzoapp:active-branch";
const ACCESS_CONTEXT_KEY_PREFIX = "mozzoapp:access-context";

const CREATOR_USER = {
  id: "creator-dev-user",
  email: "creador@local.dev",
};

const CREATOR_SESSION = {
  access_token: "creator-dev-token",
  refresh_token: "creator-dev-refresh",
  user: CREATOR_USER,
};

const CREATOR_COMPANY = {
  id: "creator-local-company",
  name: "MozzoApp Dev Company",
  slug: "mozzoapp-dev-company",
  legalName: "",
  phone: "",
  status: "active",
  role: "admin_general",
  createdAt: null,
};

const CREATOR_BRANCH = {
  id: "creator-local-branch",
  companyId: CREATOR_COMPANY.id,
  name: "Mi Sucursal Dev",
  slug: "mi-sucursal-dev",
  phone: "",
  address: "",
  description: "Modo creador local",
  timezone: "America/Argentina/Buenos_Aires",
  status: "active",
  role: "admin_general",
  availableModes: ["admin", "cocina"],
  defaultMode: "admin",
  permissions: {
    canViewMenu: true,
    canManageMenu: true,
    canViewStock: true,
    canManageStock: true,
    canManageOrders: true,
    canManageQr: true,
    canManagePayments: true,
    canManageSettings: true,
  },
  createdAt: null,
};

const WAITER_DEMO_USER = {
  id: "waiter-demo-user",
  email: "mozo.demo@local.dev",
};

const WAITER_DEMO_SESSION = {
  access_token: "waiter-demo-token",
  refresh_token: "waiter-demo-refresh",
  user: WAITER_DEMO_USER,
};

const WAITER_DEMO_COMPANY = {
  id: "waiter-demo-company",
  name: "MozzoApp Demo",
  slug: "mozzoapp-demo",
  legalName: "",
  phone: "",
  status: "active",
  role: "admin_limitado",
  workspaceRole: "mozo",
  createdAt: null,
};

const WAITER_DEMO_BRANCH = {
  id: "waiter-demo-branch",
  companyId: WAITER_DEMO_COMPANY.id,
  name: "Salon Demo",
  slug: "salon-demo",
  phone: "",
  address: "",
  description: "Modo mozo demo",
  timezone: "America/Argentina/Buenos_Aires",
  status: "active",
  role: "admin_limitado",
  availableModes: ["mozo"],
  defaultMode: "mozo",
  permissions: {
    canViewMenu: false,
    canManageMenu: false,
    canViewStock: false,
    canManageStock: false,
    canManageOrders: true,
    canManageQr: false,
    canManagePayments: false,
    canManageSettings: false,
  },
  createdAt: null,
};

const getActiveBranchStorageKey = (companyId) =>
  `${ACTIVE_BRANCH_KEY_PREFIX}:${companyId || "default"}`;

const getAccessContextStorageKey = (companyId) =>
  `${ACCESS_CONTEXT_KEY_PREFIX}:${companyId || "default"}`;

const readRequestedBranchIdFromUrl = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    return String(searchParams.get("branchId") || searchParams.get("branch") || "").trim();
  } catch (error) {
    return "";
  }
};

const readStoredBranchId = (companyId) => {
  if (typeof window === "undefined" || !companyId) {
    return "";
  }

  return window.localStorage.getItem(getActiveBranchStorageKey(companyId)) || "";
};

const persistBranchId = (companyId, branchId) => {
  if (typeof window === "undefined" || !companyId) {
    return;
  }

  window.localStorage.setItem(getActiveBranchStorageKey(companyId), branchId || "");
};

const readStoredWaiterDemoWorkspace = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(WAITER_DEMO_WORKSPACE_KEY);

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);

    if (!parsed?.company?.id || !parsed?.branch?.id) {
      return null;
    }

    return {
      company: parsed.company,
      branch: parsed.branch,
    };
  } catch (error) {
    return null;
  }
};

const persistWaiterDemoWorkspace = (company, branch) => {
  if (typeof window === "undefined" || !company?.id || !branch?.id) {
    return;
  }

  try {
    window.localStorage.setItem(
      WAITER_DEMO_WORKSPACE_KEY,
      JSON.stringify({
        company,
        branch,
      })
    );
  } catch (error) {
    // Si no se puede persistir, seguimos sin bloquear el flujo.
  }
};

const buildWaiterDemoWorkspace = (workspace) => {
  const company = workspace?.company?.id
    ? {
        ...workspace.company,
        role: "admin_limitado",
        workspaceRole: "mozo",
      }
    : WAITER_DEMO_COMPANY;

  const branch = workspace?.branch?.id
    ? {
        ...workspace.branch,
        role: "admin_limitado",
        workspaceRole: "mozo",
        availableModes: ["mozo"],
        defaultMode: "mozo",
        permissions: {
          ...WAITER_DEMO_BRANCH.permissions,
        },
        companyId: workspace.branch.companyId || company.id,
      }
    : WAITER_DEMO_BRANCH;

  return {
    company,
    branch,
  };
};

const normalizeBranchMode = (value) => {
  if (value === "admin" || value === "mozo" || value === "cocina") {
    return value;
  }

  if (
    value === "admin_limitado" ||
    value === "admin_general" ||
    value === "encargado_sucursal"
  ) {
    return "admin";
  }

  return "";
};

const getBranchAvailableModes = (branch) => {
  const hasExplicitAvailableModes = Array.isArray(branch?.availableModes);
  const modes = hasExplicitAvailableModes
    ? branch.availableModes
        .map((mode) => normalizeBranchMode(mode))
        .filter(Boolean)
    : [];

  if (hasExplicitAvailableModes) {
    return Array.from(new Set(modes));
  }

  const fallbackMode =
    normalizeBranchMode(branch?.defaultMode) || normalizeBranchMode(branch?.role);

  return fallbackMode ? [fallbackMode] : ["admin"];
};

const getDefaultBranchMode = (branch) => {
  const availableModes = getBranchAvailableModes(branch);

  if (availableModes.includes("admin")) {
    return "admin";
  }

  return availableModes[0] || "";
};

const resolveBranchSessionMode = (branch, requestedMode) => {
  const availableModes = getBranchAvailableModes(branch);
  const normalizedRequestedMode = normalizeBranchMode(requestedMode);

  if (normalizedRequestedMode && availableModes.includes(normalizedRequestedMode)) {
    return normalizedRequestedMode;
  }

  return getDefaultBranchMode(branch);
};

const readStoredAccessContext = (companyId) => {
  if (typeof window === "undefined" || !companyId) {
    return {
      mode: "",
      branchId: "",
      branchMode: "",
    };
  }

  try {
    const storageKey = getAccessContextStorageKey(companyId);
    const rawValue =
      window.sessionStorage.getItem(storageKey) ||
      window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return {
        mode: "",
        branchId: "",
        branchMode: "",
      };
    }

    const parsed = JSON.parse(rawValue);

    return {
      mode:
        parsed?.mode === "global" || parsed?.mode === "branch" ? parsed.mode : "",
      branchId: parsed?.branchId || "",
      branchMode: normalizeBranchMode(parsed?.branchMode),
    };
  } catch (error) {
    return {
      mode: "",
      branchId: "",
      branchMode: "",
    };
  }
};

const persistAccessContext = (companyId, context) => {
  if (typeof window === "undefined" || !companyId) {
    return;
  }

  const payload = {
    mode:
      context?.mode === "global" || context?.mode === "branch" ? context.mode : "",
    branchId: context?.branchId || "",
    branchMode: normalizeBranchMode(context?.branchMode),
  };

  window.sessionStorage.setItem(
    getAccessContextStorageKey(companyId),
    JSON.stringify(payload)
  );
  window.localStorage.setItem(
    getAccessContextStorageKey(companyId),
    JSON.stringify(payload)
  );
};

const clearStoredAccessContext = (companyId) => {
  if (typeof window === "undefined" || !companyId) {
    return;
  }

  const storageKey = getAccessContextStorageKey(companyId);
  window.sessionStorage.removeItem(storageKey);
  window.localStorage.removeItem(storageKey);
};

const pickActiveBranch = (branches, preferredBranchId) => {
  if (!branches?.length) {
    return null;
  }

  if (preferredBranchId) {
    const preferredBranch = branches.find((branch) => branch.id === preferredBranchId);
    if (preferredBranch) {
      return preferredBranch;
    }
  }

  return branches[0];
};

const isUserProfileComplete = (profile) =>
  Boolean(
    profile?.fullName?.trim() &&
      profile?.documentId?.trim() &&
      profile?.phone?.trim()
  );

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isCreatorMode, setIsCreatorMode] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    if (!isDeveloperBypassAllowed()) {
      window.localStorage.removeItem(CREATOR_MODE_KEY);
      return false;
    }

    if (window.localStorage.getItem(CREATOR_MODE_KEY) === "true") {
      return true;
    }

    return developerBypassAuth;
  });
  const [isWaiterDemoMode, setIsWaiterDemoMode] = useState(false);
  const isLocalMode = isCreatorMode || isWaiterDemoMode;

  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const currentBranchRef = useRef(null);
  currentBranchRef.current = currentBranch;
  const [availableBranches, setAvailableBranches] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [accessMode, setAccessMode] = useState(isCreatorMode ? "global" : "");
  const [branchSessionMode, setBranchSessionMode] = useState("");
  const [localPersonnelTestProfile, setLocalPersonnelTestProfile] = useState(() =>
    readActiveLocalPersonnelTestProfile()
  );
  const [loadingAuth, setLoadingAuth] = useState(
    isLocalMode ? false : isSupabaseConfigured
  );
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [merchantWorkspaceUpdate, setMerchantWorkspaceUpdate] = useState(null);
  const [authError, setAuthError] = useState("");
  const [needsWorkspaceSetup, setNeedsWorkspaceSetup] = useState(false);
  const [missingBranchAccess, setMissingBranchAccess] = useState(false);
  const [needsAccessSelection, setNeedsAccessSelection] = useState(false);
  const [needsUserProfileSetup, setNeedsUserProfileSetup] = useState(false);
  const workspaceHydratedRef = useRef(false);
  const workspaceRequestRef = useRef(0);
  const authUserIdRef = useRef(null);
  const isLocalPersonnelTestMode = Boolean(localPersonnelTestProfile);
  const localPersonnelTestRoleKey = String(
    localPersonnelTestProfile?.roleKey || ""
  ).trim();
  const localPersonnelTestRole = isLocalPersonnelTestMode
    ? getMozzoInternalUserRole(localPersonnelTestRoleKey)
    : null;
  const localPersonnelTestBaseBranch = isLocalPersonnelTestMode
    ? availableBranches.find(
        (branch) => branch.id === localPersonnelTestProfile?.branchId
      ) || currentBranch
    : currentBranch;
  const effectiveCurrentCompany = useMemo(() => {
    if (!isLocalPersonnelTestMode) {
      return currentCompany;
    }

    const isGlobalAdmin = localPersonnelTestRoleKey === "admin_general";
    return {
      ...currentCompany,
      role: isGlobalAdmin ? "admin_general" : "admin_limitado",
      workspaceRole: localPersonnelTestRoleKey,
    };
  }, [currentCompany, isLocalPersonnelTestMode, localPersonnelTestRoleKey]);
  const effectiveCurrentBranch = useMemo(() => {
    if (!isLocalPersonnelTestMode || !localPersonnelTestBaseBranch) {
      return currentBranch;
    }

    const branchMode =
      localPersonnelTestRoleKey === "mozo" || localPersonnelTestRoleKey === "cocina"
        ? localPersonnelTestRoleKey
        : "admin";

    return {
      ...localPersonnelTestBaseBranch,
      role:
        localPersonnelTestRoleKey === "admin_general"
          ? "admin_general"
          : "admin_limitado",
      workspaceRole: localPersonnelTestRoleKey,
      availableModes: [branchMode],
      defaultMode: branchMode,
      permissions: buildMozzoRoleBranchPermissions(
        localPersonnelTestRoleKey,
        localPersonnelTestProfile?.permissions
      ),
    };
  }, [
    currentBranch,
    isLocalPersonnelTestMode,
    localPersonnelTestBaseBranch,
    localPersonnelTestProfile?.permissions,
    localPersonnelTestRoleKey,
  ]);
  const effectiveAccessMode = isLocalPersonnelTestMode
    ? localPersonnelTestRole?.scope === "company"
      ? "global"
      : "branch"
    : accessMode;
  const effectiveBranchSessionMode = isLocalPersonnelTestMode
    ? localPersonnelTestRoleKey === "mozo" || localPersonnelTestRoleKey === "cocina"
      ? localPersonnelTestRoleKey
      : localPersonnelTestRole?.scope === "branch"
        ? "admin"
        : ""
    : branchSessionMode;
  const effectiveUser = useMemo(() => {
    if (!isLocalPersonnelTestMode) {
      return user;
    }

    return {
      ...user,
      id: localPersonnelTestProfile.id,
      email:
        localPersonnelTestProfile.email ||
        `${localPersonnelTestRoleKey || "personal"}@localhost`,
    };
  }, [isLocalPersonnelTestMode, localPersonnelTestProfile, localPersonnelTestRoleKey, user]);
  const effectiveCurrentUserProfile = useMemo(() => {
    if (!isLocalPersonnelTestMode) {
      return currentUserProfile;
    }

    return {
      ...currentUserProfile,
      fullName: localPersonnelTestProfile.fullName,
      email: localPersonnelTestProfile.email || "Sin correo - prueba local",
    };
  }, [currentUserProfile, isLocalPersonnelTestMode, localPersonnelTestProfile]);
  const isAdminGeneral = isLocalPersonnelTestMode
    ? localPersonnelTestRoleKey === "admin_general"
    : isCreatorMode ||
      currentCompany?.role === "admin_general" ||
      currentCompany?.workspaceRole === "admin_general";
  const isPlatformBackofficeAdmin = useMemo(
    () =>
      isLocalPersonnelTestMode
        ? false
        : isPlatformBackofficeAdminUser(user, { isCreatorMode }),
    [isCreatorMode, isLocalPersonnelTestMode, user]
  );
  const currentWorkspaceRole = useMemo(() => {
    if (isLocalPersonnelTestMode) {
      return localPersonnelTestRoleKey;
    }

    if (isCreatorMode) {
      return "admin_general";
    }

    if (accessMode === "branch") {
      if (branchSessionMode === "mozo" || branchSessionMode === "cocina") {
        return branchSessionMode;
      }

      if (branchSessionMode === "admin") {
        if (isAdminGeneral) {
          return "admin_general";
        }

        return currentCompany?.workspaceRole || "admin_limitado";
      }
    }

    return currentCompany?.workspaceRole || currentCompany?.role || "";
  }, [
    accessMode,
    branchSessionMode,
    currentCompany?.role,
    currentCompany?.workspaceRole,
    isAdminGeneral,
    isCreatorMode,
    isLocalPersonnelTestMode,
    localPersonnelTestRoleKey,
  ]);
  const currentAccessRole = useMemo(
    () =>
      resolveMozzoWorkspaceAccessRole({
        isCreatorMode: isCreatorMode && !isLocalPersonnelTestMode,
        isPlatformBackofficeAdmin,
        isAdminGeneral,
        accessMode: effectiveAccessMode,
        branchSessionMode: effectiveBranchSessionMode,
        currentCompany: effectiveCurrentCompany,
      }),
    [
      effectiveAccessMode,
      effectiveBranchSessionMode,
      effectiveCurrentCompany,
      isAdminGeneral,
      isCreatorMode,
      isLocalPersonnelTestMode,
      isPlatformBackofficeAdmin,
    ]
  );
  const isWaiterWorkspace =
    effectiveAccessMode === "branch" && effectiveBranchSessionMode === "mozo";
  const isKitchenWorkspace =
    effectiveAccessMode === "branch" && effectiveBranchSessionMode === "cocina";
  const currentBranchProfile = useMemo(
    () => getRestaurantProfile(effectiveCurrentBranch),
    [effectiveCurrentBranch]
  );
  const effectiveBranchServiceMode =
    effectiveCurrentBranch?.serviceMode ||
    effectiveCurrentBranch?.service_mode ||
    currentBranchProfile?.serviceMode ||
    "salon";
  const isKioskBranch =
    (!isCreatorMode || isLocalPersonnelTestMode) &&
    effectiveBranchServiceMode === "kiosk";

  const applyWorkspaceState = useCallback((workspace, preferredBranchId) => {
    const company = workspace?.company || null;
    const branches = workspace?.branches || [];
    const companyHasGlobalAdminAccess =
      company?.role === "admin_general" || company?.workspaceRole === "admin_general";
    const shouldCreateFirstBranch =
      Boolean(company) &&
      companyHasGlobalAdminAccess &&
      branches.length === 0;
    const storedAccessContext = company?.id
      ? readStoredAccessContext(company.id)
      : { mode: "", branchId: "", branchMode: "" };
    const requestedBranchId =
      preferredBranchId || storedAccessContext.branchId || readStoredBranchId(company?.id);
    const fallbackBranch = pickActiveBranch(branches, requestedBranchId);
    const shouldRestoreGlobalBranch =
      companyHasGlobalAdminAccess &&
      !storedAccessContext.mode &&
      Boolean(requestedBranchId) &&
      Boolean(fallbackBranch?.id);
    let nextBranch = fallbackBranch;
    let nextAccessMode = "";
    let nextBranchMode = "";

    if (!company || shouldCreateFirstBranch) {
      nextAccessMode = "";
    } else if (companyHasGlobalAdminAccess) {
      if (storedAccessContext.mode === "global") {
        nextAccessMode = "global";
      } else if (storedAccessContext.mode === "branch" && fallbackBranch?.id) {
        nextAccessMode = "branch";
        nextBranch = fallbackBranch;
        nextBranchMode =
          normalizeBranchMode(storedAccessContext.branchMode) === "cocina"
            ? "cocina"
            : "admin";
      } else if (shouldRestoreGlobalBranch) {
        nextAccessMode = "global";
        nextBranch = fallbackBranch;
      }
    } else if (branches.length === 1 && getBranchAvailableModes(branches[0]).length === 1) {
      nextAccessMode = "branch";
      nextBranch = branches[0];
      nextBranchMode = resolveBranchSessionMode(branches[0], storedAccessContext.branchMode);
    } else if (storedAccessContext.mode === "branch" && fallbackBranch?.id) {
      nextAccessMode = "branch";
      nextBranch = fallbackBranch;
      nextBranchMode = resolveBranchSessionMode(
        fallbackBranch,
        storedAccessContext.branchMode
      );
    }

    const shouldSelectAccess =
      Boolean(company) &&
      !shouldCreateFirstBranch &&
      branches.length > 0 &&
      !nextAccessMode;

    setCurrentCompany(company);
    setAvailableBranches(branches);
    currentBranchRef.current = nextBranch;
    setCurrentBranch(nextBranch);
    setAccessMode(nextAccessMode);
    setBranchSessionMode(nextBranchMode);
    setNeedsWorkspaceSetup(!company || shouldCreateFirstBranch);
    setMissingBranchAccess(
      Boolean(company) && branches.length === 0 && !shouldCreateFirstBranch
    );
    setNeedsAccessSelection(shouldSelectAccess);

    if (company?.id && nextBranch?.id) {
      persistBranchId(company.id, nextBranch.id);
    }

    if (company?.id && nextAccessMode) {
      persistAccessContext(company.id, {
        mode: nextAccessMode,
        branchId: nextBranch?.id || "",
        branchMode: nextBranchMode,
      });
    }

    return nextBranch;
  }, []);

  const refreshWorkspace = useCallback(
    async (targetUser, preferredBranchId, options = {}) => {
      const { silent = false } = options;
      const requestId = ++workspaceRequestRef.current;

      if (isCreatorMode) {
        setCurrentCompany(CREATOR_COMPANY);
        setCurrentBranch(CREATOR_BRANCH);
        setAvailableBranches([CREATOR_BRANCH]);
        setCurrentUserProfile({
          fullName: "Creador local",
          documentId: "",
          phone: "",
          email: CREATOR_USER.email,
        });
        setAccessMode("global");
        setBranchSessionMode("");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        workspaceHydratedRef.current = true;
        return {
          company: CREATOR_COMPANY,
          branch: CREATOR_BRANCH,
          branches: [CREATOR_BRANCH],
        };
      }

      if (isWaiterDemoMode) {
        const waiterDemoWorkspace = buildWaiterDemoWorkspace(
          readStoredWaiterDemoWorkspace()
        );

        setCurrentCompany(waiterDemoWorkspace.company);
        setCurrentBranch(waiterDemoWorkspace.branch);
        setAvailableBranches([waiterDemoWorkspace.branch]);
        setCurrentUserProfile({
          fullName: "Mozo demo",
          documentId: "",
          phone: "",
          email: WAITER_DEMO_USER.email,
        });
        setAccessMode("branch");
        setBranchSessionMode("mozo");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        workspaceHydratedRef.current = true;
        return {
          company: waiterDemoWorkspace.company,
          branch: waiterDemoWorkspace.branch,
          branches: [waiterDemoWorkspace.branch],
        };
      }

      if (!isSupabaseConfigured || !targetUser?.id) {
        setCurrentCompany(null);
        setCurrentBranch(null);
        setAvailableBranches([]);
        setCurrentUserProfile(null);
        setAccessMode("");
        setBranchSessionMode("");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        workspaceHydratedRef.current = false;
        return null;
      }

      if (!silent) {
        setLoadingWorkspace(true);
      }

      try {
        const invitationResult = await acceptPendingInvitationsForUser();
        if (requestId !== workspaceRequestRef.current) return null;
        const workspace = await getWorkspaceForUser(targetUser.id);
        if (requestId !== workspaceRequestRef.current) return null;
        const nextProfile = await getCurrentUserProfile();
        if (requestId !== workspaceRequestRef.current) return null;
        const explicitBranchId =
          preferredBranchId ||
          currentBranchRef.current?.id ||
          readRequestedBranchIdFromUrl();
        const rememberedBranchId = readStoredBranchId(workspace?.company?.id);
        const invitationBranchId =
          Number(invitationResult.acceptedCount || 0) > 0
            ? invitationResult.preferredBranchId
            : "";
        const storedBranchId =
          explicitBranchId || rememberedBranchId || invitationBranchId;
        const activeBranch = applyWorkspaceState(workspace, storedBranchId);
        const hasAdminBranchMode = (workspace?.branches || []).some((branch) =>
          getBranchAvailableModes(branch).includes("admin")
        );
        const shouldCompleteProfile =
          workspace?.company?.role === "admin_limitado" &&
          hasAdminBranchMode &&
          !isUserProfileComplete(nextProfile);

        setCurrentUserProfile(nextProfile);
        setNeedsUserProfileSetup(shouldCompleteProfile);

        setAuthError("");
        workspaceHydratedRef.current = true;

        return {
          company: workspace?.company || null,
          branch: activeBranch,
          branches: workspace?.branches || [],
        };
      } catch (error) {
        if (requestId !== workspaceRequestRef.current) return null;
        if (isMissingAuthSessionError(error)) {
          authUserIdRef.current = null;
          setSession(null);
          setUser(null);
          setLoadingAuth(false);
          setAuthError(MISSING_AUTH_SESSION_MESSAGE);
          workspaceHydratedRef.current = false;
          return null;
        }
        if (silent) {
          console.error(
            "No pudimos refrescar el workspace en segundo plano:",
            error
          );
          return null;
        }

        setCurrentCompany(null);
        setCurrentBranch(null);
        setAvailableBranches([]);
        setCurrentUserProfile(null);
        setAccessMode("");
        setBranchSessionMode("");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        setAuthError(error.message || "No se pudo cargar la empresa.");
        workspaceHydratedRef.current = false;
        return null;
      } finally {
        if (!silent && requestId === workspaceRequestRef.current) {
          setLoadingWorkspace(false);
        }
      }
    },
    [applyWorkspaceState, isCreatorMode, isWaiterDemoMode]
  );

  useEffect(() => {
    if (isLocalMode || isLocalPersonnelTestMode || !isSupabaseConfigured) return undefined;
    return subscribeToMerchantWorkspaceUpdates((detail) => {
      if (isMerchantWorkspaceUpdateForUser(detail, user, authUserIdRef.current)) {
        setMerchantWorkspaceUpdate({ branchId: detail.branchId, userId: detail.userId });
      }
    });
  }, [isLocalMode, isLocalPersonnelTestMode, user]);

  useEffect(() => {
    if (!merchantWorkspaceUpdate) return;
    if (
      isLocalMode || isLocalPersonnelTestMode || !isSupabaseConfigured ||
      !isMerchantWorkspaceUpdateForUser(merchantWorkspaceUpdate, user, authUserIdRef.current)
    ) {
      setMerchantWorkspaceUpdate(null);
      return;
    }
    // A silent refresh must not supersede a foreground load that owns the loading state.
    if (loadingAuth || loadingWorkspace) return;
    setMerchantWorkspaceUpdate(null);
    refreshWorkspace(user, undefined, { silent: true }).catch((error) => {
      console.error("No pudimos actualizar los canales del comercio:", error);
    });
  }, [
    isLocalMode, isLocalPersonnelTestMode, loadingAuth, loadingWorkspace,
    merchantWorkspaceUpdate, refreshWorkspace, user,
  ]);

  const hasBranchPermission = useCallback(
    (permissionKey) => {
      if (isCreatorMode && !isLocalPersonnelTestMode) {
        return true;
      }

      if (isAdminGeneral && effectiveAccessMode !== "branch") {
        return true;
      }

      if (!permissionKey) {
        return Boolean(effectiveCurrentBranch?.id);
      }

      if (isWaiterWorkspace) {
        return false;
      }

      if (isKitchenWorkspace) {
        return permissionKey === "canManageOrders"
          ? effectiveCurrentBranch?.permissions?.canManageOrders === true
          : false;
      }

      if (
        isAdminGeneral &&
        effectiveAccessMode === "branch" &&
        effectiveBranchSessionMode === "admin"
      ) {
        return permissionKey !== "canManageSettings";
      }

      return effectiveCurrentBranch?.permissions?.[permissionKey] === true;
    },
    [
      effectiveAccessMode,
      effectiveBranchSessionMode,
      effectiveCurrentBranch?.id,
      effectiveCurrentBranch?.permissions,
      isAdminGeneral,
      isCreatorMode,
      isKitchenWorkspace,
      isLocalPersonnelTestMode,
      isWaiterWorkspace,
    ]
  );

  const hasWaiterPermission = useCallback(
    (permissionKey) => {
      if ((isCreatorMode && !isLocalPersonnelTestMode) || isWaiterDemoMode) {
        return true;
      }

      if (!effectiveCurrentBranch?.id) {
        return false;
      }

      if (!isWaiterWorkspace) {
        if (permissionKey === "chargeOrders" || permissionKey === "refundOrders") {
          return hasBranchPermission("canManagePayments");
        }

        return hasBranchPermission("canManageOrders");
      }

      const resolvedPermissions = mapWaiterPermissionsFromBranchPermissions(
        effectiveCurrentBranch.permissions,
        {
          takeOrders: effectiveCurrentBranch?.permissions?.canManageOrders === true,
          modifyOrders: effectiveCurrentBranch?.permissions?.canManageOrders === true,
          cancelOrders: effectiveCurrentBranch?.permissions?.canManageOrders === true,
          chargeOrders: effectiveCurrentBranch?.permissions?.canManagePayments === true,
          refundOrders: effectiveCurrentBranch?.permissions?.canManagePayments === true,
        }
      );

      return resolvedPermissions[permissionKey] === true;
    },
    [
      effectiveCurrentBranch?.id,
      effectiveCurrentBranch?.permissions,
      hasBranchPermission,
      isCreatorMode,
      isLocalPersonnelTestMode,
      isWaiterDemoMode,
      isWaiterWorkspace,
    ]
  );

  const canAccessModule = useCallback(
    (moduleKey) => {
      return canAccessMozzoModule(moduleKey, {
        isCreatorMode: isCreatorMode && !isLocalPersonnelTestMode,
        isPlatformBackofficeAdmin,
        isWaiterWorkspace,
        isKitchenWorkspace,
        isAdminGeneral,
        accessMode: effectiveAccessMode,
        branchSessionMode: effectiveBranchSessionMode,
        currentBranch: effectiveCurrentBranch,
        currentCompany: effectiveCurrentCompany,
        isKioskBranch,
        hasBranchPermission,
      });
    },
    [
      effectiveAccessMode,
      effectiveBranchSessionMode,
      effectiveCurrentBranch,
      effectiveCurrentCompany,
      hasBranchPermission,
      isAdminGeneral,
      isCreatorMode,
      isKioskBranch,
      isKitchenWorkspace,
      isLocalPersonnelTestMode,
      isPlatformBackofficeAdmin,
      isWaiterWorkspace,
    ]
  );

  useEffect(() => {
    if (!isCreatorMode) {
      return;
    }

    setSession(CREATOR_SESSION);
    setUser(CREATOR_USER);
    setCurrentCompany(CREATOR_COMPANY);
    setCurrentBranch(CREATOR_BRANCH);
    setAvailableBranches([CREATOR_BRANCH]);
    setCurrentUserProfile({
      fullName: "Creador local",
      documentId: "",
      phone: "",
      email: CREATOR_USER.email,
    });
    setAccessMode("global");
    setBranchSessionMode("");
    setNeedsWorkspaceSetup(false);
    setMissingBranchAccess(false);
    setNeedsAccessSelection(false);
    setNeedsUserProfileSetup(false);
    setLoadingAuth(false);
    setAuthError("");
  }, [isCreatorMode]);

  useEffect(() => {
    if (!isWaiterDemoMode) {
      return;
    }

    const waiterDemoWorkspace = buildWaiterDemoWorkspace(
      readStoredWaiterDemoWorkspace()
    );

    setSession(WAITER_DEMO_SESSION);
    setUser(WAITER_DEMO_USER);
    setCurrentCompany(waiterDemoWorkspace.company);
    setCurrentBranch(waiterDemoWorkspace.branch);
    setAvailableBranches([waiterDemoWorkspace.branch]);
    setCurrentUserProfile({
      fullName: "Mozo demo",
      documentId: "",
      phone: "",
      email: WAITER_DEMO_USER.email,
    });
    setAccessMode("branch");
    setBranchSessionMode("mozo");
    setNeedsWorkspaceSetup(false);
    setMissingBranchAccess(false);
    setNeedsAccessSelection(false);
    setNeedsUserProfileSetup(false);
    setLoadingAuth(false);
    setAuthError("");
  }, [isWaiterDemoMode]);

  useEffect(() => {
    if (
      isCreatorMode ||
      isWaiterDemoMode ||
      !currentCompany?.id ||
      !currentBranch?.id
    ) {
      return;
    }

    persistWaiterDemoWorkspace(currentCompany, currentBranch);
  }, [currentBranch, currentCompany, isCreatorMode, isWaiterDemoMode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.removeItem(WAITER_DEMO_MODE_KEY);
    } catch {
      // Legacy demo cleanup must not prevent authentication in restricted browsers.
    }
  }, []);

  useEffect(() => {
    if (isLocalMode) {
      return undefined;
    }

    if (!isSupabaseConfigured) {
      setLoadingAuth(false);
      return undefined;
    }

    let active = true;
    let authEventReceived = false;
    const subscription = listenToAuthChanges((event, nextSession) => {
      if (!active) {
        return;
      }
      authEventReceived = true;
      const sameUser = authUserIdRef.current === nextSession?.user?.id;
      authUserIdRef.current = nextSession?.user?.id || null;
      setSession(nextSession);
      setUser(nextSession?.user || null);
      setLoadingAuth(false);
      setAuthError("");

      // Token rotation and tab refocus are not a new login. Keep the existing
      // workspace request instead of restarting it on every auth notification.
      if (sameUser && (event === "TOKEN_REFRESHED" || event === "SIGNED_IN")) return;
      const authChange = ++workspaceRequestRef.current;

      if (!nextSession?.user) {
        setLoadingWorkspace(false);
        workspaceHydratedRef.current = false;
        setCurrentCompany(null);
        setCurrentBranch(null);
        setAvailableBranches([]);
        setCurrentUserProfile(null);
        setAccessMode("");
        setBranchSessionMode("");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        return;
      }

      window.setTimeout(() => {
        if (!active || authChange !== workspaceRequestRef.current) return;
        refreshWorkspace(nextSession.user, undefined, {
          silent: workspaceHydratedRef.current,
        });
      }, 0);
    });

    const bootstrapAuth = async () => {
      try {
        const initialSession = await getInitialSession();
        if (!active || authEventReceived) return;

        authUserIdRef.current = initialSession?.user?.id || null;
        setSession(initialSession);
        setUser(initialSession?.user || null);
        setLoadingAuth(false);

        if (initialSession?.user) {
          await refreshWorkspace(initialSession.user);
        }
      } catch (error) {
        if (!active || authEventReceived) return;
        setAuthError(error.message || "No se pudo iniciar la autenticacion.");
        setLoadingAuth(false);
      }
    };

    bootstrapAuth();

    return () => {
      active = false;
      workspaceRequestRef.current += 1;
      subscription?.unsubscribe();
    };
  }, [isLocalMode, refreshWorkspace]);

  const signIn = useCallback(async ({ email, password }) => {
    setAuthError("");
    return signInWithEmail({ email, password });
  }, []);

  const signUp = useCallback(async ({ email, password, redirectTo }) => {
    setAuthError("");
    const data = await signUpWithEmail({ email, password, redirectTo });
    return {
      user: data.user || null,
      session: data.session || null,
      requiresEmailConfirmation: !data.session,
    };
  }, []);

  const signOut = useCallback(async ({ scope = "global" } = {}) => {
    setAuthError("");

    if (isLocalMode) {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(CREATOR_MODE_KEY);
        window.localStorage.removeItem(WAITER_DEMO_MODE_KEY);
      }

      setIsCreatorMode(false);
      setIsWaiterDemoMode(false);
      setSession(null);
      setUser(null);
      setCurrentCompany(null);
      setCurrentBranch(null);
      setAvailableBranches([]);
      setCurrentUserProfile(null);
      setAccessMode("");
      setBranchSessionMode("");
      setNeedsWorkspaceSetup(false);
      setMissingBranchAccess(false);
      setNeedsAccessSelection(false);
      setNeedsUserProfileSetup(false);
      return;
    }

    if (currentCompany?.id) {
      clearStoredAccessContext(currentCompany.id);
    }

    await signOutCurrentUser({ scope });
    setCurrentCompany(null);
    setCurrentBranch(null);
    setAvailableBranches([]);
    setCurrentUserProfile(null);
    setAccessMode("");
    setBranchSessionMode("");
    setNeedsWorkspaceSetup(false);
    setMissingBranchAccess(false);
    setNeedsAccessSelection(false);
    setNeedsUserProfileSetup(false);
  }, [currentCompany?.id, isLocalMode]);

  const enterCreatorMode = useCallback(() => {
    if (!isDeveloperBypassAllowed()) {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(CREATOR_MODE_KEY);
      }
      setIsCreatorMode(false);
      setAuthError("El modo creador solo esta disponible en localhost.");
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(CREATOR_MODE_KEY, "true");
      window.localStorage.removeItem(WAITER_DEMO_MODE_KEY);
    }

    clearActiveLocalPersonnelTestProfile();
    setLocalPersonnelTestProfile(null);
    setBranchSessionMode("");
    setIsWaiterDemoMode(false);
    setIsCreatorMode(true);
  }, []);

  const enterWaiterDemoMode = useCallback(() => {
    setAuthError("");

    const waiterDemoWorkspace = buildWaiterDemoWorkspace(
      readStoredWaiterDemoWorkspace()
    );

    if (typeof window !== "undefined") {
      window.localStorage.removeItem(CREATOR_MODE_KEY);
      window.localStorage.removeItem(WAITER_DEMO_MODE_KEY);
    }

    setIsCreatorMode(false);
    setIsWaiterDemoMode(true);
    setSession(WAITER_DEMO_SESSION);
    setUser(WAITER_DEMO_USER);
    setCurrentCompany(waiterDemoWorkspace.company);
    setCurrentBranch(waiterDemoWorkspace.branch);
    setAvailableBranches([waiterDemoWorkspace.branch]);
    setCurrentUserProfile({
      fullName: "Mozo demo",
      documentId: "",
      phone: "",
      email: WAITER_DEMO_USER.email,
    });
    setAccessMode("branch");
    setBranchSessionMode("mozo");
    setNeedsWorkspaceSetup(false);
    setMissingBranchAccess(false);
    setNeedsAccessSelection(false);
    setNeedsUserProfileSetup(false);
    setLoadingAuth(false);
    setLoadingWorkspace(false);
    setAuthError("");
  }, []);

  const createWorkspace = useCallback(
    async (payload) => {
      if (!user?.id) {
        throw new Error("Necesitas iniciar sesion antes de crear tu empresa.");
      }

      setAuthError("");
      setLoadingWorkspace(true);

      try {
        const workspace = currentCompany?.id
          ? await createInitialBranchForCompany(currentCompany, payload)
          : await createCompanyWithInitialBranch(user.id, payload);
        saveRestaurantProfile(workspace.branch, {
          ...getRestaurantProfile(workspace.branch),
          serviceMode: workspace.branch.serviceMode || payload.serviceMode || "salon",
          salonEnabled:
            workspace.branch.salonEnabled ?? payload.salonEnabled ?? true,
          deliveryEnabled:
            workspace.branch.deliveryEnabled ?? payload.deliveryEnabled ?? false,
          operationalSetupCompleted: true,
          waiterEnabled:
            workspace.branch.waiterEnabled ?? payload.waiterEnabled ?? true,
          stockEnabled:
            workspace.branch.stockEnabled ?? payload.stockEnabled ?? true,
          aiEnabled: workspace.branch.aiEnabled ?? payload.aiEnabled ?? true,
          wePedidosEnabled:
            workspace.branch.wePedidosEnabled ?? payload.wePedidosEnabled ?? false,
          kioskMenuMode:
            workspace.branch.kioskMenuMode || payload.kioskMenuMode || "current",
          kioskStationCount:
            workspace.branch.kioskStationCount || payload.kioskStationCount || 1,
        });
        setCurrentCompany(workspace.company);
        setCurrentBranch(workspace.branch);
        setAvailableBranches([workspace.branch]);
        setAccessMode("global");
        setBranchSessionMode("");
        setNeedsWorkspaceSetup(false);
        setMissingBranchAccess(false);
        setNeedsAccessSelection(false);
        setNeedsUserProfileSetup(false);
        persistBranchId(workspace.company.id, workspace.branch.id);
        persistAccessContext(workspace.company.id, {
          mode: "global",
          branchId: workspace.branch.id,
          branchMode: "",
        });
        return workspace;
      } catch (error) {
        setAuthError(error.message || "No se pudo crear la empresa.");
        throw error;
      } finally {
        setLoadingWorkspace(false);
      }
    },
    [currentCompany, user]
  );

  const completeUserProfile = useCallback((profile) => {
    setCurrentUserProfile(profile);
    setNeedsUserProfileSetup(false);
  }, []);

  const setActiveBranch = useCallback(
    (branchId) => {
      setCurrentBranch((previousBranch) => {
        const nextBranch =
          availableBranches.find((branch) => branch.id === branchId) || previousBranch;
        const nextBranchMode =
          accessMode === "branch"
            ? resolveBranchSessionMode(nextBranch, branchSessionMode)
            : "";

        if (nextBranch?.id && currentCompany?.id) {
          persistBranchId(currentCompany.id, nextBranch.id);
          if (accessMode) {
            persistAccessContext(currentCompany.id, {
              mode: accessMode,
              branchId: nextBranch.id,
              branchMode: nextBranchMode,
            });
          }
        }

        if (accessMode === "branch") {
          setBranchSessionMode(nextBranchMode);
        }

        currentBranchRef.current = nextBranch || null;
        return nextBranch || null;
      });
    },
    [accessMode, availableBranches, branchSessionMode, currentCompany?.id]
  );

  const enterGlobalAccess = useCallback(() => {
    if (!currentCompany?.id || !isAdminGeneral) {
      return;
    }

    const nextBranch =
      pickActiveBranch(
        availableBranches,
        currentBranch?.id || readStoredBranchId(currentCompany.id)
      ) || availableBranches[0] || null;

    currentBranchRef.current = nextBranch;
    setCurrentBranch(nextBranch);
    setAccessMode("global");
    setBranchSessionMode("");
    setNeedsAccessSelection(false);

    if (nextBranch?.id) {
      persistBranchId(currentCompany.id, nextBranch.id);
    }

    persistAccessContext(currentCompany.id, {
      mode: "global",
      branchId: nextBranch?.id || "",
      branchMode: "",
    });
  }, [availableBranches, currentBranch?.id, currentCompany?.id, isAdminGeneral]);

  const enterBranchAccess = useCallback(
    (branchId, requestedMode = "admin") => {
      if (!currentCompany?.id) {
        return false;
      }

      const nextBranch = availableBranches.find((branch) => branch.id === branchId);

      if (!nextBranch) {
        return false;
      }

      let nextBranchMode = "";

      if (isAdminGeneral) {
        nextBranchMode =
          normalizeBranchMode(requestedMode) === "cocina" ? "cocina" : "admin";
      } else {
        nextBranchMode = resolveBranchSessionMode(nextBranch, requestedMode);
      }

      if (!nextBranchMode) {
        return false;
      }

      currentBranchRef.current = nextBranch;
      setCurrentBranch(nextBranch);
      setAccessMode("branch");
      setBranchSessionMode(nextBranchMode);
      setNeedsAccessSelection(false);
      persistBranchId(currentCompany.id, nextBranch.id);
      persistAccessContext(currentCompany.id, {
        mode: "branch",
        branchId: nextBranch.id,
        branchMode: nextBranchMode,
      });
      return true;
    },
    [availableBranches, currentCompany?.id, isAdminGeneral]
  );

  const enterBranchAdminAccess = useCallback(
    (branchId) => enterBranchAccess(branchId, "admin"),
    [enterBranchAccess]
  );

  const enterBranchWaiterAccess = useCallback(
    (branchId) => enterBranchAccess(branchId, "mozo"),
    [enterBranchAccess]
  );

  const enterBranchKitchenAccess = useCallback(
    (branchId) => enterBranchAccess(branchId, "cocina"),
    [enterBranchAccess]
  );

  const resetAccessSelection = useCallback(() => {
    if (!currentCompany?.id || isCreatorMode) {
      return;
    }

    clearStoredAccessContext(currentCompany.id);
    setAccessMode("");
    setBranchSessionMode("");
    setNeedsAccessSelection(true);
  }, [currentCompany?.id, isCreatorMode]);

  const enterLocalPersonnelTestProfile = useCallback(
    (profile) => {
      if (!isLocalPersonnelTestHarnessEnabled()) {
        return false;
      }

      const roleDefinition = getMozzoInternalUserRole(profile?.roleKey);
      const assignedBranch =
        availableBranches.find((branch) => branch.id === profile?.branchId) ||
        currentBranch;

      if (!profile?.id || !profile?.fullName || !roleDefinition || !assignedBranch?.id) {
        return false;
      }

      const normalizedProfile = {
        ...profile,
        companyId: currentCompany?.id || "creator-local-company",
        roleKey: roleDefinition.key,
        branchId: assignedBranch.id,
        branchName: assignedBranch.name,
      };

      persistActiveLocalPersonnelTestProfile(normalizedProfile);
      setLocalPersonnelTestProfile(normalizedProfile);
      return true;
    },
    [availableBranches, currentBranch, currentCompany?.id]
  );

  const exitLocalPersonnelTestProfile = useCallback(() => {
    clearActiveLocalPersonnelTestProfile();
    setLocalPersonnelTestProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: effectiveUser,
      currentCompany: effectiveCurrentCompany,
      currentBranch: effectiveCurrentBranch,
      currentRestaurant: effectiveCurrentBranch,
      availableBranches,
      currentUserProfile: effectiveCurrentUserProfile,
      accessMode: effectiveAccessMode,
      branchSessionMode: effectiveBranchSessionMode,
      isAdminGeneral,
      isPlatformBackofficeAdmin,
      isKioskBranch,
      isKitchenWorkspace,
      currentWorkspaceRole,
      currentAccessRole,
      isWaiterWorkspace,
      isWaiterDemoMode,
      loadingAuth,
      loadingWorkspace,
      loadingRestaurant: loadingWorkspace,
      authError,
      needsWorkspaceSetup,
      needsRestaurantSetup: needsWorkspaceSetup,
      missingBranchAccess,
      needsAccessSelection,
      needsUserProfileSetup,
      isCreatorMode: isCreatorMode || isLocalPersonnelTestMode,
      isLocalPersonnelTestMode,
      localPersonnelTestProfile,
      isSupabaseConfigured,
      missingSupabaseConfig,
      signIn,
      signUp,
      signOut,
      enterCreatorMode,
      enterWaiterDemoMode,
      refreshWorkspace,
      refreshRestaurant: refreshWorkspace,
      createWorkspace,
      createRestaurant: createWorkspace,
      completeUserProfile,
      setActiveBranch,
      enterGlobalAccess,
      enterBranchAccess,
      enterBranchAdminAccess,
      enterBranchWaiterAccess,
      enterBranchKitchenAccess,
      resetAccessSelection,
      enterLocalPersonnelTestProfile,
      exitLocalPersonnelTestProfile,
      hasBranchPermission,
      hasWaiterPermission,
      canAccessModule,
    }),
    [
      accessMode,
      authError,
      availableBranches,
      canAccessModule,
      completeUserProfile,
      createWorkspace,
      effectiveAccessMode,
      effectiveBranchSessionMode,
      effectiveCurrentBranch,
      effectiveCurrentCompany,
      effectiveCurrentUserProfile,
      effectiveUser,
      enterBranchAccess,
      enterBranchAdminAccess,
      enterBranchWaiterAccess,
      enterBranchKitchenAccess,
      enterLocalPersonnelTestProfile,
      enterGlobalAccess,
      enterCreatorMode,
      exitLocalPersonnelTestProfile,
      hasBranchPermission,
      hasWaiterPermission,
      isAdminGeneral,
      isPlatformBackofficeAdmin,
      isCreatorMode,
      isKioskBranch,
      isKitchenWorkspace,
      isLocalPersonnelTestMode,
      isWaiterWorkspace,
      isWaiterDemoMode,
      loadingAuth,
      loadingWorkspace,
      missingBranchAccess,
      needsAccessSelection,
      needsUserProfileSetup,
      needsWorkspaceSetup,
      currentWorkspaceRole,
      currentAccessRole,
      localPersonnelTestProfile,
      refreshWorkspace,
      resetAccessSelection,
      session,
      setActiveBranch,
      signIn,
      branchSessionMode,
      signOut,
      signUp,
      enterWaiterDemoMode,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
